import type { Transport } from './transport.js';
import type { MatchSettings } from './protocol.js';

/**
 * Turn source for skirmish play: no server, no latency, no other humans.
 *
 * It still honours the command delay and turn ordering so that skirmish runs
 * through the identical lockstep pipeline as a networked match. AI commands are
 * injected as if they arrived from a remote player.
 */
export class LocalTransport implements Transport {
  readonly playerIndex = 0;
  readonly settings: MatchSettings;

  private turnHandler: ((turn: number, perPlayer: Uint8Array[]) => void) | null = null;
  private fatalHandler: ((reason: string) => void) | null = null;
  private pending = new Map<number, Uint8Array[]>();
  private aiProvider: ((turn: number, player: number) => Uint8Array) | null = null;
  private closed = false;

  constructor(settings: MatchSettings) {
    this.settings = settings;
  }

  /** Supplies commands for the non-human slots. */
  setAiProvider(fn: (turn: number, player: number) => Uint8Array): void {
    this.aiProvider = fn;
  }

  onTurn(handler: (turn: number, perPlayer: Uint8Array[]) => void): void {
    this.turnHandler = handler;
  }

  onFatal(handler: (reason: string) => void): void {
    this.fatalHandler = handler;
  }

  submit(turn: number, commands: Uint8Array, _checksum: bigint): void {
    if (this.closed) return;
    const perPlayer: Uint8Array[] = [];
    for (let p = 0; p < this.settings.playerCount; p++) {
      if (p === this.playerIndex) {
        perPlayer[p] = commands;
      } else {
        perPlayer[p] = this.aiProvider?.(turn, p) ?? new Uint8Array(0);
      }
    }
    this.pending.set(turn, perPlayer);
    // Deliver synchronously: with no network there is nothing to wait for, and
    // the game loop already paces itself against the wall clock.
    const ready = this.pending.get(turn)!;
    this.pending.delete(turn);
    this.turnHandler?.(turn, ready);
  }

  fail(reason: string): void {
    this.fatalHandler?.(reason);
  }

  close(): void {
    this.closed = true;
    this.pending.clear();
  }
}
